/**
 * Field System — component presets (Default, Inline, Search, Page, Upload).
 * Tokens → src/styles/field-tokens.css | Overview → fieldSystem.js
 */

export const FIELD_BASE =
  "input-placeholder box-border block min-w-0 font-inter text-offblack rounded-field border border-solid border-mediumgray tp-field-focus focus:outline-none placeholder-lightgray placeholder:font-inter";

/** Inline — height via token (36px → 38px @md), 12px horizontal padding */
const INLINE_HEIGHT = "h-field-inline min-h-field-inline";
const INLINE_PAD = "px-3";

const inlinePreset = (minPx, maxPx) =>
  `${FIELD_BASE} w-full min-w-[${minPx}px] max-w-[${maxPx}px] shrink-0 ${INLINE_HEIGHT} ${INLINE_PAD}`;

/** Input Field — Default: 100% up to 352px, 40 / 40 / 42px, 16px padding */
const DEFAULT_FIELD = `${FIELD_BASE} w-full max-w-field-default h-field-default min-h-field-default px-4`;

/** Search Field: 100% up to 448px, 40 / 42 / 44px, 16px padding */
const SEARCH_FIELD = `${FIELD_BASE} w-full max-w-field-search h-field-search min-h-field-search px-4`;

/** Page Field: min-h 96px, 20h / 16v padding */
const PAGE_FIELD = `${FIELD_BASE} w-full md:max-w-field-page-md xl:max-w-field-page-xl min-h-field-page px-5 py-4`;

/** Upload Area: min-h 128 / 144px, max 352px, 20px padding */
const UPLOAD_FIELD = `${FIELD_BASE} w-full max-w-field-upload min-h-field-upload md:min-h-field-upload-md xl:min-h-field-upload-md p-5`;

/** 100% width variants — same component sizing as above, no max-width cap (fills parent) */
const DEFAULT_FIELD_FULL = `${FIELD_BASE} w-full !max-w-full h-field-default min-h-field-default px-4`;
const SEARCH_FIELD_FULL = `${FIELD_BASE} w-full !max-w-full h-field-search min-h-field-search px-4`;
const PAGE_FIELD_FULL = `${FIELD_BASE} w-full !max-w-full min-h-field-page px-5 py-4`;
const UPLOAD_FIELD_FULL = `${FIELD_BASE} w-full !max-w-full min-h-field-upload md:min-h-field-upload-md xl:min-h-field-upload-md p-5`;

/** PrimeReact Chips — Default height (40 / 42px), tighter horizontal padding for tokens */
const CHIPS_SHELL = "custom-chips field-chips !flex items-center overflow-hidden";
const CHIPS_FIELD = `${FIELD_BASE} ${CHIPS_SHELL} w-full max-w-field-default px-2`;
const CHIPS_FIELD_FULL = `${FIELD_BASE} ${CHIPS_SHELL} w-full !max-w-full px-2`;

/** Maps capped variant keys → 100% width variant keys (for fullWidth prop) */
export const FIELD_FULL_WIDTH_VARIANT_MAP = {
  default: "full",
  large: "full",
  general: "full",
  medium: "full",
  small: "full",
  page: "pageFull",
  "2xl": "pageFull",
  search: "searchFull",
  extralarge: "searchFull",
  upload: "uploadFull",
  inline96: "inline96",
  inline120: "inline120",
  inline140: "inline140",
  inline160: "inline160",
  inline240: "inline240",
  micro: "inline140",
  short: "inline240",
  exsmall: "inline240",
  extrasmall01: "inline96",
  extrasmall02: "inline120",
};

/** Canonical spec presets */
export const FIELD_INLINE_96 = inlinePreset(80, 96);
export const FIELD_INLINE_120 = inlinePreset(100, 120);
export const FIELD_INLINE_140 = inlinePreset(120, 140);
export const FIELD_INLINE_160 = inlinePreset(120, 160);
export const FIELD_INLINE_240 = inlinePreset(180, 240);

export const FIELD_DEFAULT = DEFAULT_FIELD;
export const FIELD_SEARCH = SEARCH_FIELD;
export const FIELD_PAGE = PAGE_FIELD;
export const FIELD_UPLOAD = UPLOAD_FIELD;
export const FIELD_DEFAULT_FULL = DEFAULT_FIELD_FULL;
export const FIELD_SEARCH_FULL = SEARCH_FIELD_FULL;
export const FIELD_PAGE_FULL = PAGE_FIELD_FULL;
export const FIELD_UPLOAD_FULL = UPLOAD_FIELD_FULL;
export const FIELD_CHIPS = CHIPS_FIELD;
export const FIELD_CHIPS_FULL = CHIPS_FIELD_FULL;

/** Multiline page field — same sizing as PAGE_FIELD */
export const FIELD_PAGE_TEXTAREA = PAGE_FIELD;
export const FIELD_PAGE_TEXTAREA_FULL = PAGE_FIELD_FULL;

/**
 * Resolve variant key; when fullWidth is true, use the 100% parent-width preset.
 * Inline presets keep fixed widths per spec.
 */
export const resolveFieldVariant = (variant, fullWidth = false) => {
  if (!fullWidth) {
    return variant;
  }
  return FIELD_FULL_WIDTH_VARIANT_MAP[variant] ?? "full";
};

export const INPUT_FIELD_VARIANT_CLASSES = {
  /* Default */
  default: DEFAULT_FIELD,
  large: DEFAULT_FIELD,
  general: DEFAULT_FIELD,

  /* Inline presets (spec §5) */
  inline96: FIELD_INLINE_96,
  inline120: FIELD_INLINE_120,
  inline140: FIELD_INLINE_140,
  inline160: FIELD_INLINE_160,
  inline240: FIELD_INLINE_240,

  /* Component types */
  search: SEARCH_FIELD,
  upload: UPLOAD_FIELD,
  page: PAGE_FIELD,
  "2xl": PAGE_FIELD,

  /* 100% width — fills parent container (no approved max-width cap) */
  full: DEFAULT_FIELD_FULL,
  fullDefault: DEFAULT_FIELD_FULL,
  pageFull: PAGE_FIELD_FULL,
  fullPage: PAGE_FIELD_FULL,
  searchFull: SEARCH_FIELD_FULL,
  uploadFull: UPLOAD_FIELD_FULL,

  /* Legacy aliases — map to spec presets */
  medium: DEFAULT_FIELD,
  small: DEFAULT_FIELD,
  micro: FIELD_INLINE_140,
  short: FIELD_INLINE_240,
  exsmall: FIELD_INLINE_240,
  extrasmall01: FIELD_INLINE_96,
  extrasmall02: FIELD_INLINE_120,
  extralarge: SEARCH_FIELD,
};

/** Verification Code Input — apply to each digit box; wrap group with field-verification-group--* */
export const FIELD_VERIFICATION_DIGIT_CLASS =
  "field-verification-digit input-placeholder box-border font-inter text-offblack tp-field-focus focus:outline-none text-center";
